import React from "react";
import Modal from "../ui/Modal";

const ShopProductView = ({ product, onClose }) => {
  if (!product) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(price);
  };

  const getNotifyEmails = () => {
    try {
      if (!product?.notifyEmails) return [];
      if (Array.isArray(product.notifyEmails)) return product.notifyEmails;
      return JSON.parse(product.notifyEmails || "[]");
    } catch (e) {
      return [];
    }
  };

  return (
    <Modal className="max-w-xl" isOpen={true} onClose={onClose} title={"Shop Product Details"}>
      <div className="space-y-6">
        {/* Thumbnail and Video Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Thumbnail */}
          <div>
            <label className="form-label">Thumbnail</label>
            {product.thumbnail ? (
              <div className="border border-mediumgray rounded-lg overflow-hidden">
                <img
                  src={product.thumbnail}
                  alt={product.name}
                  className="w-full h-64 object-cover"
                />
              </div>
            ) : (
              <div className="border border-mediumgray rounded-lg p-4 text-center text-gray-500">
                No thumbnail available
              </div>
            )}
          </div>

          {/* Video */}
          <div>
            <label className="form-label">Video</label>
            {product.video ? (
              <div className="border border-mediumgray rounded-lg overflow-hidden">
                <video
                  src={product.video}
                  controls
                  className="w-full h-64 object-cover"
                />
              </div>
            ) : (
              <div className="border border-mediumgray rounded-lg p-4 text-center text-gray-500">
                No video available
              </div>
            )}
          </div>
        </div>

        {/* Product Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Label */}
          <div>
            <label className="form-label">Label</label>
            <p className="text-darkergray">{product.name}</p>
          </div>

          {/* Type */}
          <div>
            <label className="form-label">Type</label>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                product.type === "payment"
                  ? "bg-green-100 text-green-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {product.type === "payment" ? "Payment" : "Calendly"}
            </span>
          </div>

          {/* Price (for payment type) */}
          {product.type === "payment" && product.price && (
            <div>
              <label className="form-label">Price</label>
              <p className="text-darkergray text-lg font-semibold">
                {formatPrice(product.price)}
              </p>
            </div>
          )}

          {/* Calendly URL (for calendly type) */}
          {product.type === "calendly" && product.calendlyUrl && (
            <div>
              <label className="form-label">Calendly URL</label>
              <a
                href={product.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline break-all"
              >
                {product.calendlyUrl}
              </a>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="form-label">Description</label>
          <div className="border border-mediumgray rounded-lg p-4 bg-lightergray">
            <p className="text-darkergray whitespace-pre-wrap">
              {product.description}
            </p>
          </div>
        </div>

        {/* Order Notes & Notify Emails */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Order Notes</label>
            <p className="text-darkergray">{product.enableOrderNotes ? "Enabled" : "Disabled"}</p>
          </div>
          <div>
            <label className="form-label">Notify Emails</label>
            {getNotifyEmails().length > 0 ? (
              <div className="text-darkergray">
                <ul className="list-disc pl-5">
                  {getNotifyEmails().map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-darkergray">None</p>
            )}
          </div>
        </div>

        {/* Available To — which offices can see/buy this product */}
        <div>
          <label className="form-label">Available To</label>
          {product.offices?.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-1">
              {product.offices.map((o) => (
                <span
                  key={o?._id || o}
                  className="bg-lightgray px-3 py-1 rounded-full text-sm text-darkergray"
                >
                  {o?.name || o}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-darkergray">All offices</p>
          )}
        </div>

        {/* Timestamps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-mediumgray">
          <div>
            <label className="form-label">Created At</label>
            <p className="text-darkergray text-sm">
              {formatDate(product.createdAt)}
            </p>
          </div>
          <div>
            <label className="form-label">Updated At</label>
            <p className="text-darkergray text-sm">
              {formatDate(product.updatedAt)}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShopProductView;
